import { useEffect, useRef } from 'react'

import { useAuthStateChange } from '@/modules/auth/hooks/useAuthStateChange'
import { getHelpState } from '@/modules/help/store'
import { openSupportEventStream } from '@/services/support'
import { useSupportStore } from '../store'
import type { SupportEvent, SupportMessage } from '../types'
import { staffTypingActions } from '../typingStore'

interface StaffReplyContext {
  // True when the user is already watching this thread, so the reply lands in
  // front of them and needs no announcing.
  threadVisible: boolean
  message: SupportMessage
}

interface SupportEventsOptions {
  onStaffReply: (ticketPublicId: string, context: StaffReplyContext) => void
}

const isThreadVisible = (ticketPublicId: string) => {
  const help = getHelpState()
  const { activeTicket } = useSupportStore.getState()
  return (
    help.isOpen &&
    help.view === 'tickets' &&
    activeTicket?.publicId === ticketPublicId
  )
}

// The stream has no replay buffer, so a connect can only trust the REST state.
const reloadSupportState = () => {
  const { loaders } = useSupportStore.getState()
  loaders.fetchTickets().catch(() => {})
  const activeTicket = useSupportStore.getState().activeTicket
  if (activeTicket) loaders.fetchTicket(activeTicket.publicId).catch(() => {})
}

// Opens the support SSE stream once for the authenticated layout, bound to the
// same auth signals the websocket provider uses. Headless: mount via
// <SupportRealtime />.
export const useSupportEvents = ({ onStaffReply }: SupportEventsOptions) => {
  const closeStreamRef = useRef<(() => void) | null>(null)
  const onStaffReplyRef = useRef(onStaffReply)
  onStaffReplyRef.current = onStaffReply

  const handleEvent = (event: SupportEvent) => {
    const { actions } = useSupportStore.getState()
    switch (event.type) {
      case 'CREATE_TICKET':
        actions.upsertTicket(event.ticket)
        break
      case 'UPDATE_TICKET':
        actions.applyTicketUpdate(event.ticket)
        break
      case 'CREATE_MESSAGE': {
        actions.appendMessage(event.ticketPublicId, event.message)
        if (event.message.authorType === 'staff') {
          // A reply that has landed is no longer being typed.
          staffTypingActions().clearTyping(event.ticketPublicId)
          onStaffReplyRef.current(event.ticketPublicId, {
            threadVisible: isThreadVisible(event.ticketPublicId),
            message: event.message,
          })
        }
        break
      }
      case 'TYPING': {
        if (event.authorType !== 'staff') break
        staffTypingActions().noteTyping({
          ticketPublicId: event.ticketPublicId,
          staffName: event.staffName,
        })
        break
      }
    }
  }

  const connect = () => {
    if (closeStreamRef.current) return
    closeStreamRef.current = openSupportEventStream({
      onEvent: handleEvent,
      onResync: reloadSupportState,
    })
    // The badge has to load even where SSE is blocked, so the first load is the
    // caller's job. The stream only resyncs after it has been down.
    reloadSupportState()
  }

  const disconnect = () => {
    closeStreamRef.current?.()
    closeStreamRef.current = null
    useSupportStore.getState().actions.reset()
  }

  useAuthStateChange({
    onInitialSession: connect,
    onSignedIn: connect,
    onTokenRefreshed: connect,
    onSignedOut: disconnect,
  })

  useEffect(() => disconnect, [])
}
